/**
 * @fileoverview Página responsável por gerenciar tags utilizadas em contatos e tickets.
 */

import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import Popover from "@material-ui/core/Popover";
import Grow from "@material-ui/core/Grow";
import { Pencil, Plus, Trash2 as Trash, MoreVertical, Search } from "lucide-react";
import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";
import { Chip } from "@material-ui/core";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAG") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const GRID_COLUMNS = "40px 60px 1fr 120px 100px";
const GRID_COLUMNS_XS = "36px 50px 1fr 80px 80px";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      marginBottom: theme.spacing(1.5),
      gap: theme.spacing(1.25),
    },
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1e293b",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem",
    },
  },
  headerTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  mobileMenuButton: {
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(15, 23, 42, 0.02)",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.85)" : "#475569",
    cursor: "pointer",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    padding: 0,
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(15, 23, 42, 0.04)",
      borderColor: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.35)"
        : "rgba(59, 130, 246, 0.3)",
    },
  },
  buttonsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  cmdMenuPaper: {
    borderRadius: 14,
    overflow: "visible",
    background: theme.palette.type === "dark" ? "#1c1f22" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: theme.palette.type === "dark"
      ? "0 20px 40px -12px rgba(0, 0, 0, 0.5), 0 6px 14px -4px rgba(0, 0, 0, 0.3)"
      : "0 20px 40px -12px rgba(15, 23, 42, 0.22), 0 6px 14px -4px rgba(15, 23, 42, 0.10)",
  },
  cmdMenu: {
    display: "flex",
    flexDirection: "column",
    padding: "6px 4px",
    minWidth: 232,
    fontFamily: "'Montserrat', sans-serif",
  },
  cmdMenuSection: {
    display: "flex",
    flexDirection: "column",
  },
  cmdMenuEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    padding: "8px 12px 4px",
    color: theme.palette.type === "dark" ? "#6b7380" : "#94A3B8",
  },
  cmdMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    width: "100%",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#e6e8eb" : "#1a1a1a",
    transition: "background-color 140ms ease",
    animation: "$cmdMenuItemIn 200ms cubic-bezier(0.16, 1, 0.3, 1) both",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(15, 23, 42, 0.04)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD"}`,
      outlineOffset: 1,
    },
    "&:nth-child(1)": { animationDelay: "0ms" },
    "&:nth-child(2)": { animationDelay: "35ms" },
    "&:nth-child(3)": { animationDelay: "70ms" },
  },
  cmdMenuIconBox: {
    width: 28,
    height: 28,
    flex: "0 0 28px",
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
  },
  cmdMenuIconBoxCobalt: {
    background: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.16)" : "#EEF2FF",
    color: theme.palette.type === "dark" ? "#93a6ff" : "#3D5CFD",
  },
  cmdMenuLabel: {
    flex: 1,
  },
  "@keyframes cmdMenuItemIn": {
    "0%": { opacity: 0, transform: "translateY(4px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  addButton: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    height: 36,
    padding: "0 14px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    gap: 8,
    border: "none",
    boxShadow: "none",
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      boxShadow: "none",
    },
    "& svg": { flexShrink: 0 },
  },
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "transparent",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.02)",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(91, 124, 255, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        background:
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.04)",
        border:
          theme.palette.type === "dark"
            ? "1px solid rgba(91, 124, 255, 0.3)"
            : "1px solid rgba(0, 0, 0, 0.12)",
      },
      "&.Mui-focused": {
        background:
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.02)",
        border:
          theme.palette.type === "dark"
            ? "1px solid rgba(91, 124, 255, 0.5)"
            : "1px solid #3D5CFD",
        boxShadow:
          theme.palette.type === "dark"
            ? "0 0 0 1px rgba(91, 124, 255, 0.15)"
            : "0 0 0 1px rgba(61, 92, 253, 0.1)",
      },
      "& fieldset": {
        border: "none",
      },
    },
  },

  /* ── Bulk action bar ── */
  bulkBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    borderRadius: "12px",
    marginBottom: theme.spacing(1),
    background:
      theme.palette.type === "dark"
        ? "rgba(239, 68, 68, 0.1)"
        : "rgba(239, 68, 68, 0.06)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(239, 68, 68, 0.25)"
        : "1px solid rgba(239, 68, 68, 0.15)",
    animation: "$slideDown 0.2s ease-out",
  },
  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  bulkBarInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.875rem",
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
  },
  bulkBarActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  bulkDeleteBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    borderRadius: "8px",
    padding: "5px 16px",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(239, 68, 68, 0.2)" : "#ef4444",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#fff",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark" ? "rgba(239, 68, 68, 0.35)" : "#dc2626",
    },
  },
  bulkCancelBtn: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.8rem",
    borderRadius: "8px",
    padding: "5px 12px",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.6)"
        : "#64748b",
  },

  /* ── List layout ── */
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: theme.spacing(1),
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "12px 20px",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(0, 0, 0, 0.08)",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: GRID_COLUMNS_XS,
      padding: "10px 12px",
    },
  },
  listHeaderText: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : "#94a3b8",
  },
  listRow: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "14px 20px",
    borderRadius: "12px",
    transition: "background 0.15s ease",
    cursor: "default",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.06)"
          : "rgba(61, 92, 253, 0.04)",
    },
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.04)"
        : "1px solid rgba(0, 0, 0, 0.04)",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: GRID_COLUMNS_XS,
      padding: "10px 12px",
    },
  },
  listRowSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(239, 68, 68, 0.06)"
        : "rgba(239, 68, 68, 0.03)",
  },
  checkbox: {
    padding: 4,
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.3)"
        : "rgba(0, 0, 0, 0.25)",
    "&.Mui-checked": {
      color: theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD",
    },
  },
  tagId: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "#94a3b8",
    fontVariantNumeric: "tabular-nums",
  },
  tagNameCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  tagChip: {
    fontWeight: 600,
    fontSize: "0.85rem",
    borderRadius: "8px",
    height: 28,
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  },
  ticketsCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 12px",
    borderRadius: "8px",
    background:
      theme.palette.type === "dark"
        ? "rgba(139, 92, 246, 0.12)"
        : "rgba(139, 92, 246, 0.08)",
    color: theme.palette.type === "dark" ? "#c4b5fd" : "#7c3aed",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(139, 92, 246, 0.2)"
        : "1px solid rgba(139, 92, 246, 0.15)",
    fontSize: "0.8rem",
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    whiteSpace: "nowrap",
  },
  actionsCell: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: 6,
    borderRadius: "8px",
    transition: "all 0.15s ease",
  },
  editButton: {
    color: theme.palette.type === "dark" ? "#93c5fd" : "#3b82f6",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(59, 130, 246, 0.08)",
    },
  },
  deleteButton: {
    color: theme.palette.type === "dark" ? "#fca5a5" : "#ef4444",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(239, 68, 68, 0.15)"
          : "rgba(239, 68, 68, 0.08)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.35)"
        : "#94a3b8",
    fontSize: "0.95rem",
  },
}));

const Tags = () => {
  const classes = useStyles();
  const theme = useTheme();
  // Criar e apagar etiquetas: hoje, abertos a quem atende; editar, só o admin
  // (como a API). Com a regra nova, tags.manage para tudo.
  const { user } = useContext(AuthContext);
  const { can } = usePermission();
  const canManageTags = can("tags.manage", true);
  const canEditTags = can("tags.manage", user?.profile === "admin");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileHeaderMenuAnchor, setMobileHeaderMenuAnchor] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/tags/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchTags]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchTags();
  };

  const handleDeleteBulk = async () => {
    const ids = Array.from(selectedIds);
    try {
      await api.delete("/tags", { data: { ids } });
      toast.success(`${ids.length} tag${ids.length > 1 ? "s" : ""} excluída${ids.length > 1 ? "s" : ""}`);
    } catch (err) {
      toastError(err);
    }
    setSelectedIds(new Set());
    setBulkConfirmOpen(false);
    dispatch({ type: "RESET" });
    setSearchParam("");
    setPageNumber(1);
    await fetchTags();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const sortedTags = useMemo(
    () => tags.slice().sort((a, b) => a.id - b.id),
    [tags]
  );

  /* ── Selection helpers ── */
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected =
    sortedTags.length > 0 && sortedTags.every((t) => selectedIds.has(t.id));

  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedTags.map((t) => t.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  return (
    <MainContainer className={classes.root}>
      <ConfirmationModal
        title={
          deletingTag &&
          `${i18n.t("tags.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title="Excluir tags selecionadas"
        open={bulkConfirmOpen}
        onClose={setBulkConfirmOpen}
        onConfirm={handleDeleteBulk}
      >
        {`Tem certeza que deseja excluir ${selectedIds.size} tag${selectedIds.size > 1 ? "s" : ""}? Esta ação não pode ser desfeita.`}
      </ConfirmationModal>

      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
      />

      <div className={classes.header}>
        {isMobile ? (
          <>
            <div className={classes.headerTitleRow}>
              <Typography className={classes.title}>
                {i18n.t("tags.title")}
              </Typography>
              {/* O menu só tem o Adicionar: sem poder criar, não aparece. */}
              {canManageTags && (
                <button
                  type="button"
                  className={classes.mobileMenuButton}
                  onClick={(e) => setMobileHeaderMenuAnchor(e.currentTarget)}
                  aria-label="Mais opções"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(mobileHeaderMenuAnchor)}
                >
                  <MoreVertical size={20} strokeWidth={1.75} />
                </button>
              )}
            </div>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              className={classes.searchField}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      size={18}
                      strokeWidth={1.75}
                      color={theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.5)" : "#64748b"}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Popover
              open={Boolean(mobileHeaderMenuAnchor)}
              anchorEl={mobileHeaderMenuAnchor}
              onClose={() => setMobileHeaderMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              TransitionComponent={Grow}
              transitionDuration={{ enter: 200, exit: 140 }}
              PaperProps={{ className: classes.cmdMenuPaper }}
            >
              <div className={classes.cmdMenu} role="menu">
                {canManageTags && (
                  <div className={classes.cmdMenuSection}>
                    <div className={classes.cmdMenuEyebrow}>Adicionar</div>
                    <button
                      type="button"
                      role="menuitem"
                      className={classes.cmdMenuItem}
                      onClick={() => {
                        setMobileHeaderMenuAnchor(null);
                        handleOpenTagModal();
                      }}
                    >
                      <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxCobalt}`}>
                        <Plus size={16} strokeWidth={1.8} />
                      </span>
                      <span className={classes.cmdMenuLabel}>
                        {i18n.t("tags.buttons.add")}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </Popover>
          </>
        ) : (
          <>
            <Typography className={classes.title}>
              {i18n.t("tags.title")}
            </Typography>
            <div className={classes.buttonsWrapper}>
              <TextField
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                className={classes.searchField}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        size={18}
                        strokeWidth={1.75}
                        color={theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.5)" : "#64748b"}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {canManageTags && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenTagModal}
                  className={classes.addButton}
                  startIcon={<Plus size={15} strokeWidth={1.75} />}
                >
                  {i18n.t("tags.buttons.add")}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        {/* Bulk action bar */}
        {selectedIds.size > 0 && canManageTags && (
          <div className={classes.bulkBar}>
            <span className={classes.bulkBarInfo}>
              <Trash size={16} />
              {selectedIds.size} tag{selectedIds.size > 1 ? "s" : ""}{" "}
              selecionada{selectedIds.size > 1 ? "s" : ""}
            </span>
            <div className={classes.bulkBarActions}>
              <Button
                className={classes.bulkCancelBtn}
                size="small"
                onClick={clearSelection}
              >
                Cancelar
              </Button>
              <Button
                className={classes.bulkDeleteBtn}
                size="small"
                variant="contained"
                disableElevation
                onClick={() => setBulkConfirmOpen(true)}
              >
                Excluir selecionadas
              </Button>
            </div>
          </div>
        )}

        {/* Column headers */}
        <div className={classes.listHeader}>
          <Checkbox
            className={classes.checkbox}
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleSelectAll}
            disabled={sortedTags.length === 0 || !canManageTags}
          />
          <Typography className={classes.listHeaderText}>ID</Typography>
          <Typography className={classes.listHeaderText}>Tag</Typography>
          <Typography className={classes.listHeaderText}>
            Registros
          </Typography>
          <Typography
            className={classes.listHeaderText}
            style={{ textAlign: "right" }}
          >
            Ações
          </Typography>
        </div>

        {/* Tag rows */}
        <div className={classes.listContainer}>
          {sortedTags.length === 0 && !loading ? (
            <Typography className={classes.emptyState}>
              Nenhuma tag encontrada
            </Typography>
          ) : (
            sortedTags.map((tag) => {
              const isSelected = selectedIds.has(tag.id);
              return (
                <div
                  key={tag.id}
                  className={`${classes.listRow} ${
                    isSelected ? classes.listRowSelected : ""
                  }`}
                >
                  <Checkbox
                    className={classes.checkbox}
                    size="small"
                    checked={isSelected}
                    onChange={() => toggleSelect(tag.id)}
                    disabled={!canManageTags}
                  />

                  <Typography className={classes.tagId}>
                    #{tag.id}
                  </Typography>

                  <div className={classes.tagNameCell}>
                    <Chip
                      className={classes.tagChip}
                      style={{
                        backgroundColor: tag.color,
                        color: "#fff",
                      }}
                      label={tag.name}
                      size="small"
                    />
                  </div>

                  <div>
                    <span className={classes.ticketsCount}>
                      {tag.ticketsCount}{" "}
                      {tag.ticketsCount === 1 ? "registro" : "registros"}
                    </span>
                  </div>

                  <div className={classes.actionsCell}>
                    {canEditTags && (
                      <IconButton
                        size="small"
                        className={`${classes.iconButton} ${classes.editButton}`}
                        onClick={() => handleEditTag(tag)}
                        title="Editar"
                      >
                        <Pencil size={16} strokeWidth={1.5} />
                      </IconButton>
                    )}
                    {canManageTags && (
                      <IconButton
                        size="small"
                        className={`${classes.iconButton} ${classes.deleteButton}`}
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingTag(tag);
                        }}
                        title="Deletar"
                      >
                        <Trash size={16} strokeWidth={1.5} />
                      </IconButton>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Tags;
